import { BaseContextValue, RevisionOption } from 'features/reporting/types'
import { useRolodexEntries } from 'features/rolodex'
import { useLockManagement } from 'pages/reporting/shared/hooks/useLockManagement'
import { useMetadataEntry } from 'pages/reporting/shared/hooks/useMetadataEntry'
import { usePdfManagement } from 'pages/reporting/shared/hooks/UsePDFManagement'
import { buildReportPdfFilename } from 'pages/reporting/shared/utils/utils'
import { createContext, FC, useCallback, useContext, useMemo } from 'react'
import {
  GeckoOrganization,
  GeckoSite,
  GeckoUnit,
  RolodexEntryType,
  SignatureData,
  SignoffState,
} from 'types'

import { ffsReport } from './ffsReport'
import {
  FfsAssessmentResult,
  FfsAutocadDrawingData,
  FfsCoverSectionData,
  FfsFormDataState,
  FfsMainInfo,
} from './ffsReportData'
import { useFfsFormData } from './hooks/useFormData'
import { useIndicationsWithFfsReports } from './hooks/useIndicationsWithFfsReports'
import { useFfsReportNavigation } from './hooks/useReportNavigation'
import { IndicationWithFfsReport } from './types'

/**
 * FFS Report Context Value
 *
 * Extends BaseContextValue with FFS-specific data and handlers
 */
export interface FfsReportContextValue extends Omit<
  BaseContextValue,
  | 'inspections'
  | 'assetVizRefs'
  | 'sceneConfigs'
  | 'loadRevisionData'
  | 'copyRevisionContent'
> {
  // Form Data
  formData: FfsFormDataState
  updateFormData: (newData: Partial<FfsFormDataState>) => void
  handleSignoffFinish: (values: SignatureData) => Promise<SignoffState>
  revertFormToCantilever: () => Promise<void>
  handleMainInfoFinish: (values: FfsMainInfo) => Promise<void>
  handleCoverSectionFinish: (values: FfsCoverSectionData) => Promise<void>
  handleAutocadDrawingFinish: (values: FfsAutocadDrawingData) => Promise<void>
  handleAssessmentResultsFinish: (
    values: FfsAssessmentResult[]
  ) => Promise<void>
  handleIndicationIdsFinish: (indicationIds: string[]) => Promise<void>

  // FFS-specific data
  indicationsWithReports: IndicationWithFfsReport[]
  /** Indication IDs that were looked up by inspection tag (when no explicit IDs in form data) */
  indicationIdsFromInspection: string[]
  forceRefreshIndications: () => Promise<void>
  forceRefreshPdf: () => Promise<void>

  // Organization context
  organization: GeckoOrganization | undefined
  site: GeckoSite | undefined
  unit: GeckoUnit | undefined

  // Revisions
  selectedRevisionData: FfsFormDataState | undefined
}

interface FfsReportProviderProps {
  children: React.ReactNode
  reportId: string
  requestedMetadataEntryId: string | undefined
  siteId: string
}

export const FfsReportContext = createContext<
  FfsReportContextValue | undefined
>(undefined)

export const FfsReportProvider: FC<FfsReportProviderProps> = ({
  children,
  reportId,
  requestedMetadataEntryId,
  siteId,
}: FfsReportProviderProps) => {
  const {
    metadataEntryId,
    metadataEntries,
    isLoading: isLoadingMetadata,
    publishRevision,
    revisions,
    currentRevisionNumber,
    createNewRevision,
    isCurrentRevisionPublished,
  } = useMetadataEntry(reportId, siteId, requestedMetadataEntryId)

  const latestEntry = useMemo(() => {
    const latestRevision = revisions.find(
      (r) => r.revisionNumber === currentRevisionNumber
    )
    return metadataEntries.find((entry) => entry.id === latestRevision?.id)
  }, [metadataEntries, revisions, currentRevisionNumber])

  // Fetch organization, site, unit
  const { organizations, sites, units } = useRolodexEntries(
    latestEntry
      ? {
          type: [RolodexEntryType.REPORTING_METADATA],
          ids: [latestEntry.id],
          links: [
            RolodexEntryType.ORGANIZATION,
            RolodexEntryType.SITE,
            RolodexEntryType.UNIT,
          ],
        }
      : undefined
  )

  const { organization, site, unit } = useMemo(
    () => ({
      organization: organizations.at(0),
      site: sites.at(0),
      unit: units.at(0),
    }),
    [organizations, sites, units]
  )

  // Get inspection IDs from the metadata entry (used for fallback indication lookup)
  const inspectionIds = useMemo(() => {
    return (latestEntry?.data.inspections as string[]) || []
  }, [latestEntry])

  // Form data management
  const {
    formData,
    updateFormData,
    handleMainInfoFinish,
    handleCoverSectionFinish,
    handleAutocadDrawingFinish,
    handleAssessmentResultsFinish,
    handleIndicationIdsFinish,
    handleSignoffFinish,
  } = useFfsFormData(metadataEntryId, metadataEntries, isLoadingMetadata)

  // Fetch indications with their FFS reports
  // If formData.indicationIds is empty, falls back to looking up indications by inspection tag
  const {
    indicationsWithReports,
    indicationIdsFromInspection,
    isLoading: isLoadingIndications,
    forceRefresh: forceRefreshIndications,
  } = useIndicationsWithFfsReports({
    indicationIds: formData.indicationIds,
    inspectionIds,
  })

  const report = ffsReport
  const downloadFilename = buildReportPdfFilename({
    organizationName: organization?.data.name || formData.mainInfo.customerName,
    siteName: site?.data.name || formData.mainInfo.customerLocation,
    assetName: formData.mainInfo.assetName || unit?.data.name,
    revisionNumber: currentRevisionNumber,
  })

  // Navigation
  const { activePage, navigateToPage, returnToMenu, completionStatus } =
    useFfsReportNavigation({
      formData,
      report,
      indicationsWithReports,
    })

  // PDF Management
  const {
    latestDraftTimestamp,
    latestDraftPath,
    isPublishing,
    publishLatestDraftPDF,
    handleDownloadLatestDraft,
    handleDownloadPublishedPdf,
    handlePreviewLatestDraft,
    handlePreviewPublishedPdf,
    forceRefresh: forceRefreshPdf,
  } = usePdfManagement({
    metadataEntryId,
    formData,
    report,
    downloadFilename,
    onPublishedPathChange: (publishedPdfPath) =>
      updateFormData({ publishedPdfPath }),
  })

  // Lock management
  const {
    isLocked,
    startedSignoff,
    lastLockTimestamp,
    handleStartSignoff,
    handleUnlock,
    canStartSignoff,
    canSignAsReviewer,
  } = useLockManagement({
    metadataEntryId,
    metadataWithSignoff: {
      lockState: formData.lockState,
      signoff: formData.signoff,
    },
    updateFormData,
  })

  const { lockState, pdfState } = useMemo(
    () => ({
      lockState: { isLocked, startedSignoff, lastLockTimestamp },
      pdfState: {
        hasPublishedPdf: !!formData.publishedPdfPath,
        isPublishing,
        latestDraftTimestamp,
        latestDraftPath,
        publishedPdfPath: formData.publishedPdfPath,
      },
    }),
    [
      formData.publishedPdfPath,
      isPublishing,
      latestDraftTimestamp,
      latestDraftPath,
      isLocked,
      startedSignoff,
      lastLockTimestamp,
    ]
  )

  const revertFormToCantilever = useCallback(async () => {
    // Not applicable for FFS report
  }, [])

  // Revision management (simplified for now)
  const revisionOptions: RevisionOption[] = useMemo(
    () =>
      revisions.map((r) => ({
        id: r.id,
        revisionNumber: r.revisionNumber,
        createdAt: r.createdAt,
        publishedAt: r.publishedAt,
        isPublished: r.isPublished,
      })),
    [revisions]
  )

  const contextValue: FfsReportContextValue = useMemo(
    () => ({
      // Navigation
      activePage,
      report,
      navigateToPage,
      returnToMenu,
      completionStatus,

      // Form Data
      formData,
      updateFormData,
      handleSignoffFinish,
      revertFormToCantilever,
      handleMainInfoFinish,
      handleCoverSectionFinish,
      handleAutocadDrawingFinish,
      handleAssessmentResultsFinish,
      handleIndicationIdsFinish,

      // FFS-specific
      indicationsWithReports,
      indicationIdsFromInspection,
      forceRefreshIndications,

      // Metadata
      metadataEntryId,
      isLoading: isLoadingMetadata || isLoadingIndications,
      currentRevisionNumber,
      unit,
      organization,
      site,

      // Submissions (simplified)
      isSubmitting: false,
      handleSubmitReport: async () => {},
      handleSignoff: async (signatureData: SignatureData) => {
        await handleSignoffFinish(signatureData)
      },
      handlePublishRevision: async () => {
        await publishRevision()
      },

      // Revisions
      revisions: revisionOptions,
      selectedRevisionData: undefined,
      hasPublishedRevisions: revisions.some((r) => r.isPublished),
      canRevertToCantilever: false,
      createNewRevision,
      isCurrentRevisionPublished,

      // Lock Management
      lockState,
      handleStartSignoff,
      handleUnlock,
      canStartSignoff,
      canSignAsReviewer,

      // PDF Management
      pdfState,
      publishLatestDraftPDF,
      handleDownloadLatestDraft,
      handleDownloadPublishedPdf,
      handlePreviewLatestDraft,
      handlePreviewPublishedPdf,
      forceRefreshPdf,

      // Not using cantilever data for FFS
      hasCantileverData: false,
    }),
    [
      activePage,
      report,
      navigateToPage,
      returnToMenu,
      completionStatus,
      formData,
      updateFormData,
      handleSignoffFinish,
      revertFormToCantilever,
      handleMainInfoFinish,
      handleCoverSectionFinish,
      handleAutocadDrawingFinish,
      handleAssessmentResultsFinish,
      handleIndicationIdsFinish,
      indicationsWithReports,
      indicationIdsFromInspection,
      forceRefreshIndications,
      metadataEntryId,
      isLoadingMetadata,
      isLoadingIndications,
      currentRevisionNumber,
      unit,
      organization,
      site,
      publishRevision,
      revisionOptions,
      revisions,
      createNewRevision,
      isCurrentRevisionPublished,
      lockState,
      handleStartSignoff,
      handleUnlock,
      canStartSignoff,
      canSignAsReviewer,
      pdfState,
      publishLatestDraftPDF,
      handleDownloadLatestDraft,
      handleDownloadPublishedPdf,
      handlePreviewLatestDraft,
      handlePreviewPublishedPdf,
      forceRefreshPdf,
    ]
  )

  return (
    <FfsReportContext.Provider value={contextValue}>
      {children}
    </FfsReportContext.Provider>
  )
}

export const useFfsReportOps = () => {
  const context = useContext(FfsReportContext)
  if (!context) {
    throw new Error('useFfsReportOps must be used within a FfsReportProvider')
  }
  return context
}
